import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, Bookmark, X, Sparkles, Wand2, Globe, Rss, FolderPlus, Plus } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

export default function News() {
  const { settings, refresh: refreshSettings } = useSettings() || {};
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [busyDiscover, setBusyDiscover] = useState(false);
  const [busyRss, setBusyRss] = useState(false);
  const [busyClassify, setBusyClassify] = useState(false);
  const [savedOnly, setSavedOnly] = useState(false);
  const [topic, setTopic] = useState("all");
  const [busySummary, setBusySummary] = useState({});
  const [sourceFilter, setSourceFilter] = useState("all"); // all | ai | rss
  const [savingHeadline, setSavingHeadline] = useState(null); // news item being saved
  const [newProjectName, setNewProjectName] = useState("");
  const [busySave, setBusySave] = useState(false);
  const autoSynced = useRef(false);

  const load = async () => {
    const { data } = await api.get("/news", {
      params: {
        ...(savedOnly ? { saved_only: true } : {}),
        ...(topic !== "all" ? { topic } : {}),
      },
    });
    setItems(data);
  };
  const loadProjects = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data || []);
    } catch (err) { console.error("Could not load projects for news", err); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [savedOnly, topic]);
  useEffect(() => { loadProjects(); }, []);

  const discover = async ({ silent = false } = {}) => {
    setBusyDiscover(true);
    try {
      const { data } = await api.post("/news/discover");
      if (!silent) {
        toast.success(`AI found ${data.inserted} fresh headline${data.inserted === 1 ? "" : "s"}`);
      } else if (data.inserted > 0) {
        toast.success(`${data.inserted} new headline${data.inserted === 1 ? "" : "s"} scouted for you`);
      }
      await load();
      if (refreshSettings) await refreshSettings();
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI discovery failed";
      if (!silent) toast.error(msg);
    } finally {
      setBusyDiscover(false);
    }
  };

  const refreshRss = async () => {
    setBusyRss(true);
    try {
      const { data } = await api.post("/news/refresh");
      toast.success(`RSS: added ${data.inserted} from ${data.feeds} feed${data.feeds === 1 ? "" : "s"}`);
      await load();
    } catch { toast.error("RSS refresh failed"); }
    finally { setBusyRss(false); }
  };

  const classify = async () => {
    setBusyClassify(true);
    try {
      const { data } = await api.post("/news/classify");
      toast.success(`Classified ${data.classified} headlines · tagged ${data.with_topics}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Classify failed"); }
    finally { setBusyClassify(false); }
  };

  const dismiss = async (id) => { await api.put(`/news/${id}`, { dismissed: true }); load(); };
  const toggleSave = async (n) => { await api.put(`/news/${n.id}`, { saved: !n.saved }); load(); };
  const summarise = async (n) => {
    setBusySummary((s) => ({ ...s, [n.id]: true }));
    try {
      const { data } = await api.post(`/news/${n.id}/summarise`);
      setItems((all) => all.map((x) => (x.id === n.id ? { ...x, summary: data.summary } : x)));
      toast.success("Summary generated");
    } catch { toast.error("Failed"); }
    finally { setBusySummary((s) => ({ ...s, [n.id]: false })); }
  };

  const saveToProject = async (project_id, project_name) => {
    if (!savingHeadline) return;
    setBusySave(true);
    try {
      const { data } = await api.post(`/news/${savingHeadline.id}/save-to-project`, {
        project_id: project_id || null,
        project_name: project_name || null,
        as_task: true,
      });
      toast.success(
        data.created_project
          ? `Project created and task added`
          : `Saved to project — task added`
      );
      setSavingHeadline(null);
      setNewProjectName("");
      if (data.created_project) await loadProjects();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusySave(false);
    }
  };

  // Auto-discover on first load if last discovery was >20h ago.
  useEffect(() => {
    if (!settings || autoSynced.current) return;
    autoSynced.current = true;
    const last = settings.last_news_discovery_at;
    const stale = !last || (Date.now() - new Date(last).getTime()) > 20 * 60 * 60 * 1000;
    if (stale && (settings.news_topics || []).length > 0) {
      // Fire and forget — silent unless it finds new items.
      discover({ silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const topics = settings?.news_topics || [];
  const lastDiscover = settings?.last_news_discovery_at;
  const filtered = items.filter((n) => {
    if (sourceFilter === "ai") return n.source_kind === "ai_web_search";
    if (sourceFilter === "rss") return n.source_kind !== "ai_web_search";
    return true;
  });

  return (
    <div className="space-y-6" data-testid="news-page">
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold tracking-tight">News</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Claude scouts the live web for fresh headlines matching your topics — plus any RSS feeds you've added in Settings.
          </p>
          <div className="mt-2 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> Last AI scout: <b>{timeAgo(lastDiscover) || "never"}</b></span>
            {busyDiscover && <span className="text-[hsl(var(--studio-primary))] animate-pulse">· scouting now…</span>}
          </div>
        </div>
        <div className="md:ml-auto flex items-center gap-2 flex-wrap">
          <Button
            data-testid="news-discover-btn"
            onClick={() => discover({ silent: false })}
            disabled={busyDiscover}
            className="bg-[hsl(var(--studio-primary))] hover:bg-[hsl(var(--studio-primary-hover))]"
          >
            <Sparkles className={`w-4 h-4 mr-2 ${busyDiscover ? "animate-pulse" : ""}`} />
            {busyDiscover ? "Scouting…" : "Discover fresh"}
          </Button>
          {(settings?.news_feeds || []).length > 0 && (
            <Button data-testid="news-refresh-rss-btn" variant="outline" onClick={refreshRss} disabled={busyRss}>
              <Rss className={`w-4 h-4 mr-2 ${busyRss ? "animate-spin" : ""}`} /> RSS
            </Button>
          )}
          <Button data-testid="news-classify-btn" variant="outline" onClick={classify} disabled={busyClassify}>
            <Wand2 className={`w-4 h-4 mr-2 ${busyClassify ? "animate-pulse" : ""}`} /> Auto-tag
          </Button>
          <Button data-testid="news-saved-toggle" variant={savedOnly ? "default" : "outline"} onClick={() => setSavedOnly((s) => !s)}>
            <Bookmark className="w-4 h-4 mr-2" /> {savedOnly ? "Saved" : "All"}
          </Button>
        </div>
      </div>

      {/* Source + Topic filter pills */}
      <div className="flex flex-wrap gap-1.5" data-testid="source-pills">
        {[
          { key: "all", label: "All sources" },
          { key: "ai", label: "AI web-search", icon: Globe },
          { key: "rss", label: "RSS", icon: Rss },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              data-testid={`source-pill-${s.key}`}
              onClick={() => setSourceFilter(s.key)}
              className={`text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${sourceFilter === s.key ? "bg-foreground text-background border-transparent" : "border-border text-muted-foreground hover:bg-accent"}`}
            >
              {Icon && <Icon className="w-3 h-3" />} {s.label}
            </button>
          );
        })}
      </div>

      {topics.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="topic-pills">
          <button
            data-testid="topic-pill-all"
            onClick={() => setTopic("all")}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${topic === "all" ? "bg-[hsl(var(--studio-primary))] text-white border-transparent" : "border-border text-muted-foreground hover:bg-accent"}`}
          >All</button>
          {topics.map((t) => (
            <button
              key={t}
              data-testid={`topic-pill-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => setTopic(t)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${topic === t ? "bg-[hsl(var(--studio-primary))] text-white border-transparent" : "border-border text-muted-foreground hover:bg-accent"}`}
            >{t}</button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <Card className="studio-card p-10 text-center" data-testid="news-empty-state">
          <div className="text-lg font-medium">No headlines yet</div>
          <p className="text-sm text-muted-foreground mt-1">
            Press <b>Discover fresh</b> to have Claude scout the live web for headlines on your topics.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((n) => {
            const isAI = n.source_kind === "ai_web_search";
            return (
              <Card key={n.id} className="studio-card p-5" data-testid={`news-card-${n.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <a href={n.url} target="_blank" rel="noreferrer" className="font-medium hover:underline">{n.headline}</a>
                    <div className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge
                        variant={isAI ? "default" : "secondary"}
                        className={`text-[10px] font-normal inline-flex items-center gap-1 ${isAI ? "bg-[hsl(var(--halo-teal)/0.15)] text-[hsl(var(--halo-teal))] border-transparent" : ""}`}
                        data-testid={`news-source-badge-${isAI ? "ai" : "rss"}`}
                      >
                        {isAI ? <Globe className="w-2.5 h-2.5" /> : <Rss className="w-2.5 h-2.5" />} {n.source}
                      </Badge>
                      {n.published_at && <span>{new Date(n.published_at).toLocaleDateString()}</span>}
                      {(n.topics || []).map((t) => (
                        <Badge key={t} className="text-[10px] font-normal bg-[hsl(var(--studio-primary)/0.10)] text-[hsl(var(--studio-primary))] border-transparent">{t}</Badge>
                      ))}
                    </div>
                    {n.summary && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{n.summary}</p>}
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => summarise(n)} disabled={busySummary[n.id]}><Sparkles className="w-3.5 h-3.5 mr-1" /> {busySummary[n.id] ? "…" : "Summarise"}</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`news-save-to-project-${n.id}`}
                        title="Save to project"
                      >
                        <FolderPlus className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground">
                        Save headline to project
                      </DropdownMenuLabel>
                      {projects.length === 0 && (
                        <div className="px-2 py-2 text-xs text-muted-foreground">No projects yet.</div>
                      )}
                      {projects.slice(0, 8).map((p) => (
                        <DropdownMenuItem
                          key={p.id}
                          data-testid={`save-to-project-option-${p.id}`}
                          onSelect={() => { setSavingHeadline(n); saveToProject(p.id); }}
                        >
                          <span className="truncate">{p.title}</span>
                          {p.status && <span className="ml-auto text-[10px] uppercase text-muted-foreground">{p.status}</span>}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        data-testid={`save-to-new-project-${n.id}`}
                        onSelect={(e) => { e.preventDefault(); setSavingHeadline(n); }}
                      >
                        <Plus className="w-3.5 h-3.5 mr-2" /> Create new project…
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button variant="ghost" size="sm" onClick={() => toggleSave(n)} data-testid={`news-save-${n.id}`}><Bookmark className={`w-3.5 h-3.5 ${n.saved ? "fill-current" : ""}`} /></Button>
                  {n.url && <a href={n.url} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm"><ExternalLink className="w-3.5 h-3.5" /></Button></a>}
                  <Button variant="ghost" size="sm" onClick={() => dismiss(n.id)}><X className="w-3.5 h-3.5" /></Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create-new-project dialog */}
      <Dialog
        open={!!savingHeadline}
        onOpenChange={(open) => { if (!open) { setSavingHeadline(null); setNewProjectName(""); } }}
      >
        <DialogContent data-testid="save-to-project-dialog">
          <DialogHeader>
            <DialogTitle>Save headline to a new project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground line-clamp-2">
              {savingHeadline?.headline}
            </p>
            <Input
              autoFocus
              placeholder="Project name — e.g. Cannes 2026 pitch"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              data-testid="new-project-name-input"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newProjectName.trim() && !busySave) {
                  saveToProject(null, newProjectName.trim());
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setSavingHeadline(null); setNewProjectName(""); }}
            >Cancel</Button>
            <Button
              data-testid="create-project-and-save-btn"
              disabled={!newProjectName.trim() || busySave}
              onClick={() => saveToProject(null, newProjectName.trim())}
            >
              {busySave ? "Creating…" : "Create project & add task"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
